/**
 * Reusable pointer-drag value scrubber for spacing edits.
 *
 * Used by both the box-model widget side labels and the canvas spacing badges,
 * so dragging a label or a crosshatch zone produces identical results.
 *
 * Features:
 *   - Iframe-zoom compensation: pointer delta divides by zoom so a 10px drag
 *     at 50% zoom yields a 20px change (same as resize handles).
 *   - Modifier keys: Shift = step to next/prev token, Alt = fine (×0.1),
 *     Cmd/Ctrl = bypass token snap.
 *   - Token snap: snaps live value to the nearest entry in a px ladder
 *     within a slack window, bypassable with Cmd/Ctrl.
 *   - Esc cancellation: pointermove handler sees the ESC key via document
 *     keydown bridged through the abort callback.
 */

import { startPointerDrag } from '../shared/pointer-drag.js';

const DEFAULT_TOKEN_PX_LADDER = Object.freeze([0, 4, 8, 12, 16, 24, 32, 48, 64, 96, 128]);

const SNAP_PX_SLACK = 6;

/**
 * D10 — the coarse detent. 1px of finger travel mapping to 1px of value is below
 * the ~8px jitter floor of a fingertip, and the finger occludes the value pill
 * while it happens. A doubled slack turns the scrub into a detent gesture the
 * hand can actually land.
 *
 * Chosen per EVENT, not per device (D6). A hybrid laptop reports a coarse
 * pointer while a mouse is plugged in, and that mouse should keep its precision.
 */
const SNAP_PX_SLACK_COARSE = 12;

let cachedTokens = null;

export function readDesignTokenLadder(rootElement) {
  if (cachedTokens) return cachedTokens;
  const root = rootElement || (typeof document !== 'undefined' ? document.documentElement : null);
  if (!root || typeof getComputedStyle !== 'function') {
    cachedTokens = DEFAULT_TOKEN_PX_LADDER.slice();
    return cachedTokens;
  }
  const style = getComputedStyle(root);
  const collected = new Set([0]);
  for (let i = 1; i <= 12; i++) {
    const raw = style.getPropertyValue(`--pb-space-${i}`).trim();
    const px = parsePxLength(raw, 16);
    if (px != null) collected.add(Math.round(px));
  }
  if (collected.size <= 1) {
    cachedTokens = DEFAULT_TOKEN_PX_LADDER.slice();
    return cachedTokens;
  }
  // Add a few common upper-range tokens so dragging beyond the spacing scale
  // still snaps somewhere predictable.
  for (const extra of [48, 64, 96, 128]) collected.add(extra);
  cachedTokens = Array.from(collected).sort((a, b) => a - b);
  return cachedTokens;
}

export function resetDesignTokenCache() {
  cachedTokens = null;
}

function parsePxLength(value, rootPx) {
  if (typeof value !== 'string' || !value) return null;
  const lower = value.trim().toLowerCase();
  const num = Number.parseFloat(lower);
  if (!Number.isFinite(num)) return null;
  if (lower.endsWith('rem')) return num * (rootPx || 16);
  if (lower.endsWith('em')) return num * (rootPx || 16);
  if (lower.endsWith('px') || /^[-\d.]+$/.test(lower)) return num;
  return null;
}

export function snapToTokenLadder(px, ladder = DEFAULT_TOKEN_PX_LADDER, slack = SNAP_PX_SLACK) {
  if (!Array.isArray(ladder) || ladder.length === 0)
    return { value: px, snapped: false, hit: null };
  let best = null;
  let bestDist = Infinity;
  for (const token of ladder) {
    const dist = Math.abs(px - token);
    if (dist < bestDist) {
      bestDist = dist;
      best = token;
    }
  }
  if (best != null && bestDist <= slack) {
    return { value: best, snapped: true, hit: best };
  }
  return { value: px, snapped: false, hit: best };
}

export function nextTokenStep(px, ladder = DEFAULT_TOKEN_PX_LADDER, direction = 1) {
  if (!Array.isArray(ladder) || ladder.length === 0) return px + direction;
  const sorted = ladder.slice().sort((a, b) => a - b);
  if (direction > 0) {
    for (const token of sorted) {
      if (token > px + 0.001) return token;
    }
    return sorted[sorted.length - 1] + 8;
  }
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i] < px - 0.001) return sorted[i];
  }
  return sorted[0] - 8;
}

/**
 * Begin a scrub drag.
 *
 * @param {Object} opts
 * @param {PointerEvent} opts.event           - the pointerdown event
 * @param {'x'|'y'} opts.axis                 - axis the drag operates on
 * @param {number} opts.startPx               - starting value in px
 * @param {number} [opts.zoom=1]              - iframe zoom factor
 * @param {number} [opts.sign=1]              - +1 or -1: direction of growth
 * @param {number} [opts.min]                 - clamp minimum
 * @param {number} [opts.max]                 - clamp maximum
 * @param {boolean} [opts.snap=true]          - snap to design-token ladder
 * @param {number[]} [opts.ladder]            - custom px ladder
 * @param {Function} opts.onMove              - (info) => void per move
 * @param {Function} [opts.onEnd]             - (info) => void on commit
 * @param {Function} [opts.onCancel]          - () => void on Esc/blur
 * @returns {{cancel: Function, end: Function}}
 */
export function beginScrub(opts) {
  const {
    event,
    axis,
    startPx,
    zoom = 1,
    sign = 1,
    min = -Infinity,
    max = Infinity,
    snap = true,
    ladder,
    onMove,
    onEnd,
    onCancel
  } = opts || {};

  if (!event || typeof onMove !== 'function') {
    return { cancel: () => {}, end: () => {} };
  }

  const startCoord = axis === 'x' ? event.clientX : event.clientY;
  const safeZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const tokens = ladder || readDesignTokenLadder();
  // One decision per gesture, taken from the press that started it.
  const coarseGesture = !!event.pointerType && event.pointerType !== 'mouse';
  const snapSlack = coarseGesture ? SNAP_PX_SLACK_COARSE : SNAP_PX_SLACK;
  let lastPx = startPx;
  let moved = false;

  function applyMove(e) {
    const coord = axis === 'x' ? e.clientX : e.clientY;
    const rawDelta = (coord - startCoord) * sign;
    const delta = rawDelta / safeZoom;
    const fineMode = e.altKey;
    const stepMode = e.shiftKey;
    // Cmd/Ctrl has no touch equivalent, so on a finger this would be
    // permanently false anyway — forcing it makes that explicit rather than
    // incidental, and stops a stray modifier from a paired keyboard turning the
    // detent off mid-gesture.
    const bypassSnap = !coarseGesture && (e.metaKey || e.ctrlKey);

    let nextPx = startPx + (fineMode ? delta * 0.1 : delta);
    if (stepMode && !fineMode) {
      // Step to next token boundary based on cumulative delta direction.
      const dir = delta >= 0 ? 1 : -1;
      const stepped = nextTokenStep(startPx, tokens, dir);
      nextPx = Math.abs(delta) > 1 ? stepped : startPx;
    }

    nextPx = Math.max(min, Math.min(max, nextPx));

    let snapped = false;
    if (snap && !bypassSnap && !stepMode) {
      const result = snapToTokenLadder(nextPx, tokens, snapSlack);
      if (result.snapped) {
        nextPx = result.value;
        snapped = true;
      }
    }

    lastPx = nextPx;
    moved = true;
    e.preventDefault();
    onMove({
      px: nextPx,
      delta,
      snapped,
      modifiers: { fineMode, stepMode, bypassSnap },
      event: e
    });
  }

  // Mobile P17 — the listener set is now the shared contract, and ONLY the
  // listener set changed. Every line of the snap/step/fine math above is
  // untouched, which is what lets box-model-widget, glass-spacing-badges,
  // glass-shared-spacing and section-spacing-drawer inherit the fix with zero
  // edits of their own.
  //
  // What they gain: pointercancel, lostpointercapture and pointer capture,
  // none of which existed here — a scrub interrupted by an incoming call or an
  // app switch used to leave the widget mid-drag forever. And on touch the
  // gesture now arms on a long press, so a swipe that starts on a spacing
  // label scrolls the page instead of silently scrubbing a value.
  //
  // activationPx: 0 keeps the scrub immediate. The helper's 5px default is
  // right for a reorder (where a stray 3px must not tear a section out of the
  // page) and wrong here, where the first pixel of travel is the point.
  const drag = startPointerDrag(event, {
    activationPx: 0,
    onMove: ({ event: e }) => applyMove(e),
    onEnd: ({ event: e }) => {
      if (typeof onEnd === 'function') onEnd({ px: lastPx, event: e || null });
    },
    onCancel: () => {
      if (typeof onCancel === 'function') onCancel();
    }
  });

  // The helper fires onEnd/onCancel only for a drag that actually STARTED —
  // correct for a reorder, where a gesture that never moved must not commit
  // anything. This API has always settled unconditionally, and its callers
  // depend on that: section-spacing-drawer's onCancel is what removes the
  // `.pb-spsd-dragging` classes and clears `activeScrub`, so swallowing it
  // after a no-move press would leave the drawer stuck in its dragging skin.
  // Settle by hand in exactly that case.
  const settleUnmoved = (fn) => {
    drag.cancel();
    if (typeof fn === 'function') fn({ px: lastPx, event: null });
  };

  return {
    cancel() {
      if (!moved) {
        settleUnmoved(onCancel);
        return;
      }
      drag.cancel();
    },
    end() {
      if (!moved) {
        settleUnmoved(onEnd);
        return;
      }
      drag.end();
    }
  };
}
